import { useState, useEffect } from "react";
import {
  OAuthProvider,
  signInWithCredential,
  signInWithPopup,
} from "firebase/auth";
import { auth } from "../firebase.js";
import config from "../config.js";

export const useAppleSignIn = () => {
  const isDev = process.env.NODE_ENV === "development";
  const [isAppleSdkLoaded, setIsAppleSdkLoaded] = useState(isDev);
  const [status, setStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isDev) return; // Skip loading Apple JS SDK locally

    let isMounted = true;
    const initApple = () => {
      if (globalThis.AppleID && isMounted && config.appleServiceId) {
        try {
          globalThis.AppleID.auth.init({
            clientId: config.appleServiceId,
            scope: "name email",
            redirectURI: config.appleRedirectUri,
            state: "origin:web",
            usePopup: true, // Use the smooth Apple native popup
          });
          setIsAppleSdkLoaded(true);
        } catch (err) {
          console.warn("Failed to initialize Apple SDK:", err);
        }
      }
    };

    if (globalThis.AppleID) {
      initApple();
    } else {
      const interval = setInterval(() => {
        if (globalThis.AppleID) {
          initApple();
          clearInterval(interval);
        }
      }, 500);
      return () => {
        isMounted = false;
        clearInterval(interval);
      };
    }
  }, [isDev]);

  const signInDev = async () => {
    setStatus("Redirecting to Apple via Firebase...");
    const provider = new OAuthProvider("apple.com");
    provider.addScope("email");
    provider.addScope("name");
    provider.setCustomParameters({ locale: "en" });

    console.log("useAppleSignIn (Dev): Calling signInWithPopup...");
    const result = await signInWithPopup(auth, provider);
    console.log("useAppleSignIn (Dev): Popup successful", result.user);
    setStatus("Signed in!");
  };

  const signInProd = async () => {
    setStatus("Waiting for Apple...");

    const messagePromise = new Promise((resolve, reject) => {
      const handleMessage = (event) => {
        // Verify message origin securely
        if (
          !event.origin ||
          (event.origin !== window.location.origin &&
            event.origin !== "https://appleid.apple.com")
        ) {
          return;
        }

        if (event.data?.type === "APPLE_SIGN_IN_RESPONSE") {
          window.removeEventListener("message", handleMessage);
          const payload = event.data.payload;
          if (payload.error) {
            reject(new Error(payload.error));
          } else {
            resolve(payload.id_token);
          }
        }
      };
      window.addEventListener("message", handleMessage);
    });

    const response = await Promise.race([
      globalThis.AppleID.auth
        .signIn()
        .then((res) => res?.authorization?.id_token),
      messagePromise,
    ]);

    if (!response) {
      throw new Error("No ID token returned from Apple.");
    }

    setStatus("Authenticating with Firebase...");
    const provider = new OAuthProvider("apple.com");
    const credential = provider.credential({ idToken: response });

    console.log("useAppleSignIn (Prod): Calling signInWithCredential...");
    const result = await signInWithCredential(auth, credential);
    console.log("useAppleSignIn (Prod): Auth successful", result.user);
    setStatus("Signed in!");
  };

  const handleSignInError = (error) => {
    console.error("Sign-In Error:", error);

    if (
      error?.error === "popup_closed_by_user" ||
      error?.code === "auth/popup-closed-by-user" ||
      (error instanceof Error && error.message.includes("popup-closed-by-user"))
    ) {
      setStatus("");
      return;
    }

    let errorMessage = "Unknown error";
    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === "object" && error !== null) {
      errorMessage = error.error || error.message || JSON.stringify(error);
    } else if (typeof error === "string") {
      errorMessage = error;
    }
    setStatus(`Error: ${errorMessage}`);
  };

  const signIn = async () => {
    if (!isAppleSdkLoaded && !isDev) {
      setStatus("Error: Apple SDK not loaded");
      return;
    }

    try {
      setIsLoading(true);
      if (isDev) {
        await signInDev();
      } else {
        await signInProd();
      }
    } catch (error) {
      handleSignInError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isAppleSdkLoaded,
    status,
    setStatus,
    isLoading,
    signIn,
  };
};
